import { addMinutes, differenceInMinutes, format, isSameDay, startOfDay } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import React, { useEffect, useRef } from "react";

import type {
	RecurringSeriesModelJson,
	UpdateRecurringSeriesRequestJson,
	VideoConferencingProviderEnum,
} from "@performyard/service-contracts";

import MeetingForm, { type MeetingFormData, type MeetingRecurrenceFormData } from "./MeetingForm";

import { useCurrentUser, useEditWorkspaceRecurringSeries } from "../../../../../api/hooks";
import { useCalendarIntegrationStatus, useVideoConferencingEligibility } from "../../hooks";

import { hasMeetingStarted } from "../../../utils/meetingTiming";

import { type RepeatsPreset, recurringMeetingFormSchema } from "./meeting-form-schema";
import { buildRecurrenceFromForm, repeatsFromRecurrence } from "./meeting-form-utils";
import { useMeetingFormState } from "./use-meeting-form-modal";

type EditRecurringSeriesModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspaceId: string;
	series: RecurringSeriesModelJson | null;
};

type ScheduleSnapshot = {
	repeats: RepeatsPreset;
	startDateTimeMs: number;
	durationMinutes: number;
	name: string;
	description: string;
	carryOver: boolean;
	seriesEndDateMs: number | null;
	addToCalendar: boolean;
	videoConferencingProvider: VideoConferencingProviderEnum | null;
};

export const EditRecurringSeriesModal = ({
	isOpen,
	handleClose,
	workspaceId,
	series,
}: EditRecurringSeriesModalProps): React.ReactElement => {
	const { mutate: editWorkspaceRecurringSeries, isPending } = useEditWorkspaceRecurringSeries(handleClose);
	const { isCalendarIntegrationEnabled } = useCalendarIntegrationStatus();
	const {
		eligible: isVideoConferencingEligible,
		adminEnabled: isVideoConferencingAdminEnabled,
		connectedProviders,
	} = useVideoConferencingEligibility();
	const { data: currentUser } = useCurrentUser();
	// The master lives on the series owner's calendar, so only they can be told their own
	// calendar follows the edit — a non-owner's edit still syncs, just not to their calendar.
	const isSeriesCalendarOwner =
		!!series?.integrationOwnerId &&
		series.integrationOwnerId === (currentUser?.me?.authorization?.payload.user_id || "");
	// "Add Series to My Calendar" means the actor's own calendar: a non-owner facing an
	// existing linkage would see the owner's state as their own checkbox, and unchecking
	// it would detach the owner's calendar — hide the toggle until no master exists.
	const canToggleSeriesCalendar = isSeriesCalendarOwner || !series?.hasCalendarSeries;
	// Conferencing binds to the same owner, and updateSeries rebinds that owner to the actor only
	// while the series is neither on nor headed for someone's calendar. Past that point a
	// non-owner's choice would put every future meeting on the owner's Zoom/Teams account, or
	// move the owner's pending calendar attach onto the actor's calendar.
	const canManageSeriesVideoConferencing =
		isSeriesCalendarOwner || (!series?.hasCalendarSeries && !series?.addToCalendar);

	const { formValues, validationErrors, updateForm, setNewDefaultValues, validateForm, resetValidationErrors } =
		useMeetingFormState(recurringMeetingFormSchema);

	const initializedSeriesIdRef = useRef<string | null>(null);
	const initialSnapshotRef = useRef<ScheduleSnapshot | null>(null);

	useEffect(() => {
		if (isOpen && series && initializedSeriesIdRef.current !== series._id) {
			// Prefill in the series' anchor zone — the editor's browser zone may disagree on the day.
			const occurrenceInstant = new Date(series.upcomingMeetingStartDateTime ?? series.nextOccurrenceAt);
			const occurrenceStart = utcToZonedTime(occurrenceInstant, series.timezone);
			// Add the duration to the instant, not the zoned value, so DST-spanning occurrences keep their length.
			const occurrenceEnd = utcToZonedTime(
				addMinutes(occurrenceInstant, series.durationMinutes),
				series.timezone
			);
			const seriesEndDate = series.seriesEndDate
				? utcToZonedTime(series.seriesEndDate, series.timezone)
				: undefined;

			setNewDefaultValues({
				name: series.name,
				startDate: startOfDay(occurrenceStart),
				endDate: startOfDay(occurrenceEnd),
				startTime: format(occurrenceStart, "HH:mm"),
				endTime: format(occurrenceEnd, "HH:mm"),
				description: series.description,
				carryOver: series.carryOver,
				addToCalendar: series.addToCalendar,
				videoConferencingProvider: series.videoConferencingProvider ?? null,
				workspaceId,
				repeats: repeatsFromRecurrence(series.recurrence),
				seriesEnds: seriesEndDate ? "onDate" : "never",
				seriesEndDate,
			});
			initialSnapshotRef.current = {
				repeats: repeatsFromRecurrence(series.recurrence),
				startDateTimeMs: occurrenceStart.getTime(),
				durationMinutes: series.durationMinutes,
				name: series.name,
				description: series.description,
				carryOver: series.carryOver,
				seriesEndDateMs: seriesEndDate ? seriesEndDate.getTime() : null,
				addToCalendar: series.addToCalendar,
				videoConferencingProvider: series.videoConferencingProvider ?? null,
			};
			resetValidationErrors();
			initializedSeriesIdRef.current = series._id;
		} else if (!isOpen) {
			initializedSeriesIdRef.current = null;
		}
	}, [isOpen, series, workspaceId, setNewDefaultValues, resetValidationErrors]);

	// Show the stored recurrence label until the schedule moves; then the date-derived label applies.
	const initialSchedule = initialSnapshotRef.current;
	const showsStoredSchedule =
		!!initialSchedule &&
		formValues.repeats === initialSchedule.repeats &&
		!!formValues.startDate &&
		isSameDay(formValues.startDate, new Date(initialSchedule.startDateTimeMs));

	const handleSubmit = (data: MeetingFormData, recurrence: MeetingRecurrenceFormData): void => {
		if (!series || !validateForm()) {
			return;
		}

		const initial = initialSnapshotRef.current;
		const seriesTimezone = series.timezone;
		// Duration comes from the mapped instants — across DST the wall clocks are an hour off the real length.
		const durationMinutes = differenceInMinutes(
			zonedTimeToUtc(data.endDateTime, seriesTimezone),
			zonedTimeToUtc(data.startDateTime, seriesTimezone)
		);
		const initialProvider = initial?.videoConferencingProvider ?? null;
		// Clearing a persisted provider is valid even after that provider disconnects. The
		// form retains that value until the user explicitly clears it; the started-occurrence
		// and company gates still prevent a hidden/automatic clear from becoming destructive.
		const canDeselectInitialProvider =
			!!initialProvider &&
			isVideoConferencingAdminEnabled &&
			!hasMeetingStarted(zonedTimeToUtc(data.startDateTime, seriesTimezone));
		const selectedProviderIsConnected =
			!!data.videoConferencingProvider &&
			connectedProviders.some(({ provider }) => provider === data.videoConferencingProvider);
		// Rebuilding the pattern from the picked date is lossy ("last Tuesday" re-reads as "fourth")
		// and scheduleStartDate re-anchors the series, so a time-only edit must send neither.
		const patternTouched =
			!initial ||
			recurrence.repeats !== initial.repeats ||
			!isSameDay(data.startDateTime, new Date(initial.startDateTimeMs));
		// Presets only map intervals 1–2; keep a stored custom interval unless the preset itself changed.
		const rebuiltRecurrence = buildRecurrenceFromForm(recurrence.repeats, data.startDateTime);
		const nextRecurrence =
			initial && recurrence.repeats === initial.repeats
				? { ...rebuiltRecurrence, interval: series.recurrence.interval }
				: rebuiltRecurrence;
		const scheduleTouched = !initial || patternTouched || data.startDateTime.getTime() !== initial.startDateTimeMs;
		const durationChanged = !initial || durationMinutes !== initial.durationMinutes;
		const effectiveEndDateMs = recurrence.seriesEndDate ? recurrence.seriesEndDate.getTime() : null;
		const endDateChanged = !initial || effectiveEndDateMs !== initial.seriesEndDateMs;

		// Form dates are in the series' zone; only the instants sent back need converting out of it.
		const formattedData: UpdateRecurringSeriesRequestJson = {
			...(data.name !== initial?.name && { name: data.name }),
			...(data.description !== initial?.description && { description: data.description }),
			...(data.carryOver !== initial?.carryOver && { carryOver: data.carryOver }),
			...(scheduleTouched && {
				...(patternTouched && {
					recurrence: nextRecurrence,
					scheduleStartDate: zonedTimeToUtc(data.startDateTime, seriesTimezone).toISOString(),
				}),
				startTimeOfDay: format(data.startDateTime, "HH:mm"),
			}),
			...((scheduleTouched || durationChanged) && { durationMinutes }),
			...(endDateChanged && {
				seriesEndDate: recurrence.seriesEndDate
					? zonedTimeToUtc(recurrence.seriesEndDate, seriesTimezone).toISOString()
					: null,
			}),
			// Only an eligible editor's explicit toggle is sent — the form force-unchecks the
			// box when the integration is unusable, and forwarding that would silently detach
			// the series' calendar just because a non-connected editor saved the modal. The
			// owner gate mirrors the hidden toggle: a non-owner's form state never detaches
			// the owner's linkage.
			...(isCalendarIntegrationEnabled &&
				canToggleSeriesCalendar &&
				data.addToCalendar !== initial?.addToCalendar && { addToCalendar: data.addToCalendar }),
			// Adding or switching still requires a live provider. Clearing a persisted
			// disconnected provider is allowed only while the visible owner controls are
			// usable; unrelated saves retain that value in MeetingForm.
			...(canManageSeriesVideoConferencing &&
				data.videoConferencingProvider !== initialProvider &&
				(data.videoConferencingProvider
					? isVideoConferencingEligible &&
						selectedProviderIsConnected && { videoConferencingProvider: data.videoConferencingProvider }
					: canDeselectInitialProvider && { removeVideoConferencing: true })),
		};

		if (Object.keys(formattedData).length === 0) {
			handleClose();
			return;
		}

		editWorkspaceRecurringSeries({ workspaceId, seriesId: series._id, data: formattedData });
	};

	const onClose = (): void => {
		handleClose();
		resetValidationErrors();
	};

	return (
		<MeetingForm
			isOpen={isOpen}
			onClose={onClose}
			onSubmit={handleSubmit}
			formValues={formValues}
			onFormChange={updateForm}
			validationErrors={validationErrors}
			submitLabel="Save Changes"
			isPending={isPending}
			title="Edit Recurring Meeting Details"
			subtitle={
				series?.addToCalendar &&
				series?.hasCalendarSeries &&
				isCalendarIntegrationEnabled &&
				isSeriesCalendarOwner
					? "Changes apply to upcoming meetings only. Any updates to this meeting will automatically update in your connected calendar. Past meetings won't change."
					: "Changes apply to upcoming meetings only. Past meetings won't change."
			}
			showCarryOver
			carryOverVariant="series"
			showMeetingIntegrations={false}
			showSeriesCalendarToggle={canToggleSeriesCalendar}
			showSeriesVideoConferencing={canManageSeriesVideoConferencing && isVideoConferencingAdminEnabled}
			persistedVideoConferencingProvider={series?.videoConferencingProvider ?? null}
			formTimezone={series?.timezone}
			showRecurrence
			selectedRepeatsLabel={showsStoredSchedule ? series?.recurrenceLabel : undefined}
			startDateLabel="Next Meeting Start"
		/>
	);
};
